import { useMemo, useState } from "react";

export type SortDir = "asc" | "desc";

/**
 * 列表客户端排序。点击列头在「升 → 降 → 取消」间循环。
 * 取消（sortKey=null）时回到数据原始顺序（接口默认排序）。
 *
 * 比较规则：number / boolean 数值比较，其余按本地化字符串比较；
 * null / undefined / 空串恒沉底（与升降无关）。
 *
 * 用法：
 *   const { sorted, sortKey, sortDir, toggle } = useSortable(rows ?? []);
 *   <SortableTh sortKey="date" {...{ sortKey: sortKey, sortDir, toggle }}>日期</SortableTh>
 *   sorted.slice((page-1)*pageSize, page*pageSize)
 */
export function useSortable<T extends Record<string, unknown>>(rows: T[]) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const toggle = (key: string) => {
    if (sortKey !== key) { setSortKey(key); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    setSortKey(null); setSortDir("asc"); // 第三次点击取消排序
  };

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const dir = sortDir === "asc" ? 1 : -1;
    const isEmpty = (v: unknown) => v === null || v === undefined || v === "";
    return [...rows].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (isEmpty(av) && isEmpty(bv)) return 0;
      if (isEmpty(av)) return 1;  // 空值恒沉底
      if (isEmpty(bv)) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      if (typeof av === "boolean" && typeof bv === "boolean") return (Number(av) - Number(bv)) * dir;
      return String(av).localeCompare(String(bv), "zh-CN") * dir;
    });
  }, [rows, sortKey, sortDir]);

  return { sorted, sortKey, sortDir, toggle };
}
